/**
 * Acceso físico e ingreso (AF §6) — esquemas compartidos entre backend, SPA, app de
 * socios y agente de acceso.
 *
 * Lo que ya vivía en `schemas/devices.ts` (`accessValidateBody`, `accessSnapshotResponse`,
 * `contingencySyncBody`) sigue ahí porque es el contrato §3 del **dispositivo**; acá van
 * los esquemas del módulo: el semáforo, la excepción, el registro de ingresos, los
 * visitantes por convenio, la prueba, el fichaje y la contingencia.
 *
 * **Formatos de QR (RN-ACC-03/06)** — ratificados con la app de socios y el agente:
 * - Carnet del socio:      `viva:member:<memberNumber>:<totp>`
 * - Carnet del visitante:  `viva:visitor:<docNumber>:<totp>`
 * - QR impreso del local:  `viva:location:<accessPointId>:<secret>`
 * - QR impreso del salón:  `viva:room:<roomId>:<secret>` (lo valida Clases, RN-CLA-30)
 */
import { z } from "zod";
import { ACCESS_METHODS, ACCESS_PERSON_KINDS, ACCESS_REASONS, ACCESS_RESULTS, PLATFORMS, TIMECLOCK_DIRECTIONS, TIMECLOCK_METHODS, } from "../enums.js";
import { dateISO, listQuery, objectId, pagination, reasonRequired, yearMonth } from "./base.js";
// ─────────────────────────────────────────────────────────────────────────────
// Formatos de QR — una sola fuente para la app, el agente y el backend
// ─────────────────────────────────────────────────────────────────────────────
/** RN-ACC-06: lo que la app dibuja y el lector del molinete manda tal cual. */
export const memberQrPayload = (memberNumber, code) => `viva:member:${memberNumber}:${code}`;
/** RN-ACC-19: el visitante por convenio no tiene número de socio; su clave es el documento. */
export const visitorQrPayload = (docNumber, code) => `viva:visitor:${docNumber}:${code}`;
/** RN-ACC-03: QR estático impreso del local sin recepción. */
export const locationQrPayload = (accessPointId, secret) => `viva:location:${accessPointId}:${secret}`;
/**
 * RN-CLA-30: QR estático impreso del salón, el que el socio escanea para el check-in.
 * Es el mismo texto que imprime el cartel del portal (Estructura) y el que valida Clases:
 * **se define acá una sola vez** para que las dos puntas no puedan volver a divergir
 * (divergieron: el cartel llevaba el `roomId` y el check-in sólo aceptaba el secreto pelado).
 */
export const roomQrPayload = (roomId, secret) => `viva:room:${roomId}:${secret}`;
export const MEMBER_QR_RE = /^viva:member:(\d+):([A-Za-z0-9]{6,10})$/;
export const VISITOR_QR_RE = /^viva:visitor:([A-Za-z0-9.-]{5,20}):([A-Za-z0-9]{6,10})$/;
export const LOCATION_QR_RE = /^viva:location:([0-9a-fA-F]{24}):([A-Za-z0-9_-]{16,128})$/;
/**
 * Los tres textos que un lector puede entregar para un salón, en un solo regex:
 * `viva:room:<roomId>:<secret>` (el cartel impreso), `viva:room:<secret>` y el secreto
 * pelado — los dos últimos porque hay lectores que recortan el prefijo y carteles viejos.
 * El `roomId` es opcional: cuando viene, quien resuelve tiene que exigir que coincida.
 */
export const ROOM_QR_RE = /^(?:viva:room:)?(?:([0-9a-fA-F]{24}):)?(.+)$/;
/** Parte un QR de salón. `null` si no hay secreto que buscar. */
export function parseRoomQr(raw) {
    const match = ROOM_QR_RE.exec(raw.trim());
    if (!match)
        return null;
    const secret = (match[2] ?? "").trim();
    // El secreto es base64url: si quedaron dos puntos adentro, lo leído no es un QR de salón
    // (o es uno truncado) y no vale la pena ir a la base a buscarlo.
    if (!secret || secret.includes(":"))
        return null;
    return { roomId: match[1] ?? null, secret };
}
// ─────────────────────────────────────────────────────────────────────────────
// Semáforo (RN-ACC-08)
// ─────────────────────────────────────────────────────────────────────────────
export const ACCESS_LIGHTS = ["green", "yellow", "red"];
/** Pasos de RN-ACC-08, en el orden en que se evalúan y se cortan. */
export const ACCESS_CHECK_STEPS = [
    "identity",
    "admission",
    "freeze",
    "debt",
    "medical",
    "coverage",
    "location_window",
];
export const accessCheck = z.object({
    step: z.enum(ACCESS_CHECK_STEPS),
    ok: z.boolean(),
    detail: z.string().optional(),
});
/**
 * Badges del semáforo (AF §6, pantalla «Semáforo de ingreso»).
 * `courtesy_pct:<n>` es el único paramétrico: lleva el porcentaje del beneficio.
 */
export const ACCESS_BADGES = [
    "minor_authorized",
    "minor_not_authorized",
    "visitor_sportclub",
    "visitor_wellhub",
    "trial",
    "health_flag",
    "dni_only",
];
export const evaluateAccessResult = z.object({
    light: z.enum(ACCESS_LIGHTS),
    /** Texto del AF, tal como lo muestra el semáforo: «cuota vencida», «local cerrado»… */
    reason: z.string().nullable(),
    /** Código estable del motivo, para que la app y la SPA decidan la acción. */
    reasonCode: z.enum(ACCESS_REASONS).nullable(),
    checks: z.array(accessCheck),
    badges: z.array(z.string()),
});
/** Ficha que ve recepción en cada lectura (AF §6, pantalla del semáforo). */
export const accessPersonCard = z.object({
    kind: z.enum(ACCESS_PERSON_KINDS),
    id: objectId.nullable(),
    name: z.string(),
    memberNumber: z.number().int().nullable(),
    docNumber: z.string().nullable(),
    initials: z.string(),
    photoUrl: z.string().nullable(),
    badges: z.array(z.string()),
});
// ─────────────────────────────────────────────────────────────────────────────
// Validación en el punto de acceso (RN-ACC-02/04/06/07)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * `POST /api/access/validate` — lo manda el agente (contrato §3).
 * `identifier` es el template/dedo enrolado (huella) o el payload completo del QR.
 */
export const accessValidateFullBody = z.object({
    accessPointId: objectId,
    method: z.enum(["fingerprint", "member_qr"]),
    identifier: z.string().min(1).max(512),
    /** Momento de la lectura del hardware; si falta, el server usa el suyo. */
    at: dateISO.optional(),
});
export const accessValidateFullResponse = z.object({
    result: z.enum(ACCESS_RESULTS),
    reason: z.string().nullable(),
    reasonCode: z.enum(ACCESS_REASONS).nullable(),
    person: accessPersonCard.nullable(),
    accessLogId: objectId,
    /** Fichaje de personal: el molinete abre pero NO corre RN-ACC-08 (RN-ACC-22). */
    timeclock: z
        .object({ entryId: objectId, direction: z.enum(TIMECLOCK_DIRECTIONS), staffName: z.string() })
        .nullable()
        .default(null),
    checks: z.array(accessCheck).default([]),
});
/** `POST /api/access/manual` — habilitación manual desde recepción (RN-ACC-07). */
export const manualAccessBody = z
    .object({
    accessPointId: objectId,
    docNumber: z.string().trim().min(4).max(20).optional(),
    memberNumber: z.coerce.number().int().positive().optional(),
    personId: objectId.optional(),
    personKind: z.enum(["member", "visitor"]).optional(),
})
    .refine((v) => Boolean(v.docNumber ?? v.memberNumber ?? v.personId), {
    message: "Buscá a la persona por DNI, número de socio o id",
});
/**
 * `POST /api/access/exceptions` — excepción puntual (RN-ACC-14).
 * Vale para ESE ingreso: no salda deuda, no extiende el apto, no toca el plan.
 * La autoriza encargado+ con su sesión, o con `authorizerUserId` + `pin` en el mostrador.
 */
export const accessExceptionBody = z.object({
    /** El evento rechazado que recepción le señala al encargado. */
    accessLogId: objectId,
    reason: reasonRequired,
    authorizerUserId: objectId.optional(),
    pin: z
        .string()
        .regex(/^\d{4,8}$/)
        .optional(),
});
/** `POST /api/access/agreement-entry` — ingreso de visitante por convenio (RN-ACC-19). */
export const agreementEntryBody = z.object({
    accessPointId: objectId,
    visitorId: objectId.optional(),
    docNumber: z.string().trim().min(4).max(20).optional(),
    platform: z.enum(PLATFORMS).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Registro de ingresos (RN-ACC-15/17)
// ─────────────────────────────────────────────────────────────────────────────
export const accessLogsQuery = listQuery.extend({
    locationId: objectId.optional(),
    accessPointId: objectId.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
    method: z.enum(ACCESS_METHODS).optional(),
    result: z.enum(ACCESS_RESULTS).optional(),
    personKind: z.enum(ACCESS_PERSON_KINDS).optional(),
    personId: objectId.optional(),
    platform: z.enum(PLATFORMS).optional(),
    /** Sólo los ingresos con excepción (RN-ACC-14), resaltados en la pantalla. */
    onlyExceptions: z.coerce.boolean().optional(),
    /** Sólo los cargados en modo contingencia (RN-ACC-24). */
    onlyContingency: z.coerce.boolean().optional(),
});
export const accessLogsExportQuery = accessLogsQuery.extend({
    format: z.enum(["csv"]).default("csv"),
});
/** RN-ACC-17: respaldo propio para conciliar contra la base de la plataforma (§9). */
export const agreementReportQuery = z.object({
    month: yearMonth,
    platform: z.enum(PLATFORMS).optional(),
    branchId: objectId.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Huellas (RN-ACC-05) y modo de acceso (RN-SOC-06)
// ─────────────────────────────────────────────────────────────────────────────
/** Los 10 dedos: se puede registrar cualquiera (y todos) — RN-ACC-05, cambio de sep-2026. */
export const FINGERS = [
    "right_thumb",
    "right_index",
    "right_middle",
    "right_ring",
    "right_little",
    "left_thumb",
    "left_index",
    "left_middle",
    "left_ring",
    "left_little",
];
/**
 * RN-ACC-05: cuántos dedos puede tener registrados una persona. Eran 2; desde sep-2026
 * (pedido de Damián) son todos: más dedos = más chances de que el molinete lea alguno.
 */
export const MAX_FINGERS = FINGERS.length;
/**
 * Qué es el dato biométrico que se enrola:
 * - `template`: plantilla del algoritmo de un lector/controladora, o el id que devuelve
 *   la controladora (PV-36). Chica (≤ 20.000 caracteres).
 * - `image_png`: la IMAGEN de la huella en PNG sin pérdida, tal como la entrega el
 *   lector ZKTeco por WebUSB (300×375 px a 500 dpi, ~90 KB). Es lo que guarda el
 *   mostrador: de la imagen se genera después la plantilla que pida el molinete, del
 *   fabricante que sea, sin volver a enrolar a nadie.
 */
export const FINGERPRINT_FORMATS = ["template", "image_png"];
/** Tope de la imagen en base64: holgado para 300×375 (~120 KB) y lectores más grandes. */
export const FINGERPRINT_IMAGE_MAX_CHARS = 600_000;
const TEMPLATE_MAX_CHARS = 20_000;
export const enrollFingerprintBody = z
    .object({
    finger: z.enum(FINGERS),
    /** Base64 del dato biométrico (según `format`), o el id de la controladora. */
    template: z.string().min(4).max(FINGERPRINT_IMAGE_MAX_CHARS),
    format: z.enum(FINGERPRINT_FORMATS).default("template"),
    /**
     * Local donde se tomó. Opcional: sin él se usa el local con recepción de la sede
     * activa y, si la sede no tiene locales cargados, queda registrada la sede.
     */
    locationId: objectId.optional(),
    /** Re-enrolar el mismo dedo pisa el anterior; queda auditado (RN-ACC-05). */
    replace: z.boolean().default(false),
    /**
     * Plantilla ZKFinger v10 (base64) de la misma muestra, la que entrega el programa del
     * lector de la PC (`puente`). Se guarda cifrada para el molinete ZKTeco y se encola al
     * equipo de la sede del socio. Sólo acompaña a la imagen (`image_png`).
     */
    zkTemplate: z
        .string()
        .trim()
        .min(16)
        .max(TEMPLATE_MAX_CHARS)
        .regex(/^[A-Za-z0-9+/]+={0,2}$/, "La plantilla del molinete no es base64.")
        .optional(),
})
    .superRefine((body, ctx) => {
    if (body.zkTemplate !== undefined && body.format !== "image_png") {
        ctx.addIssue({
            code: "custom",
            path: ["zkTemplate"],
            message: "La plantilla del molinete viaja sólo junto con la imagen del lector.",
        });
    }
    if (body.format === "template" && body.template.length > TEMPLATE_MAX_CHARS) {
        ctx.addIssue({
            code: "custom",
            path: ["template"],
            message: `Una plantilla no puede superar ${TEMPLATE_MAX_CHARS} caracteres.`,
        });
    }
    // Firma de PNG en base64 («\x89PNG» → «iVBORw0KGgo»): rechaza basura antes de cifrarla.
    if (body.format === "image_png" && !body.template.startsWith("iVBORw0KGgo")) {
        ctx.addIssue({
            code: "custom",
            path: ["template"],
            message: "La imagen de la huella no es un PNG válido.",
        });
    }
});
export const fingerprintDto = z.object({
    finger: z.enum(FINGERS),
    enrolledAt: z.coerce.date(),
    locationId: objectId.nullable(),
    byUserId: objectId.nullable(),
    /** Handle NO secreto: es lo que el punto de acceso manda como `identifier`. */
    templateRef: z.string(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Visitantes por convenio y clase de prueba (RN-ACC-18/20)
// ─────────────────────────────────────────────────────────────────────────────
/** Alta express en la tablet kiosko (RN-ACC-18). */
export const visitorExpressBody = z.object({
    platform: z.enum(PLATFORMS),
    identity: z.object({
        docType: z.enum(["dni", "passport", "foreign_id"]).default("dni"),
        docNumber: z.string().trim().min(4).max(20),
    }),
    firstName: z.string().trim().min(2).max(60),
    lastName: z.string().trim().min(2).max(60),
    email: z.email().optional(),
    phone: z.string().trim().max(30).default(""),
    /** D4: recepción ve la declaración marcada antes de habilitar el paso. */
    health: z.object({
        hasInjuries: z.boolean().default(false),
        hasCardiacCondition: z.boolean().default(false),
        isPregnant: z.boolean().default(false),
        notes: z.string().max(1000).default(""),
    }),
    imageConsent: z.boolean(),
    acceptedRules: z.literal(true),
    /** Firma manuscrita de la tablet, en base64 (va a S3, nunca al disco). */
    signatureImage: z.string().max(2_000_000).optional(),
    /** RN-SOC-40: vigencia y apto verificados en la app de origen. */
    verifiedInPlatform: z.boolean().default(false),
    branchId: objectId.optional(),
    /** Registrar además el ingreso «convenio» del día. */
    registerEntry: z.boolean().default(true),
    accessPointId: objectId.optional(),
});
/** Registro de la clase de prueba desde la tablet (RN-ACC-20). */
export const trialEntryBody = z.object({
    dni: z.string().trim().min(4).max(20),
    name: z.string().trim().min(2).max(120),
    phone: z.string().trim().min(6).max(30),
    email: z.email().optional(),
    age: z.coerce.number().int().min(10).max(100).optional(),
    /** Declaración de condición física; obligatoria para habilitar el paso (D4). */
    health: z.object({
        hasInjuries: z.boolean().default(false),
        hasCardiacCondition: z.boolean().default(false),
        isPregnant: z.boolean().default(false),
        notes: z.string().max(1000).default(""),
    }),
    sourceCode: z.string().trim().min(2).max(40).default("reception"),
    accessPointId: objectId,
    /** Si Clases/Caja ya crearon la clase y el cargo, se ata acá (RN-PLA-22). */
    sessionId: objectId.optional(),
    teacherId: objectId.optional(),
    chargeId: objectId.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Fichaje (RN-ACC-21..23)
// ─────────────────────────────────────────────────────────────────────────────
/** `POST /api/kiosk/timeclock` — PIN o QR personal en la tablet (RN-PRO-06). */
export const kioskTimeclockBody = z
    .object({
    method: z.enum(["pin", "personal_qr"]),
    userId: objectId.optional(),
    pin: z
        .string()
        .regex(/^\d{4,8}$/)
        .optional(),
    qr: z.string().min(6).max(256).optional(),
    /** Si falta, se deduce del sentido de la última fichada del día. */
    direction: z.enum(TIMECLOCK_DIRECTIONS).optional(),
    locationId: objectId.optional(),
})
    .refine((v) => (v.method === "pin" ? Boolean(v.userId && v.pin) : Boolean(v.qr)), {
    message: "El PIN necesita usuario y clave; el QR personal, el código leído",
});
/** `POST /api/teachers/timeclock` — desde el celular, locales sin molinete (PV-41). */
export const teacherTimeclockBody = z.object({
    locationId: objectId,
    direction: z.enum(TIMECLOCK_DIRECTIONS).optional(),
});
/** RN-ACC-23: la corrección es una entrada NUEVA vinculada; la original nunca se pisa. */
export const timeclockCorrectionBody = z.object({
    direction: z.enum(TIMECLOCK_DIRECTIONS),
    ts: dateISO,
    reason: reasonRequired,
    locationId: objectId.optional(),
});
export const timeclockQuery = pagination.extend({
    staffId: objectId.optional(),
    branchId: objectId.optional(),
    locationId: objectId.optional(),
    month: yearMonth.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
    onlyCorrections: z.coerce.boolean().optional(),
});
export const timeclockEntryDto = z.object({
    id: objectId,
    staffId: objectId,
    staffName: z.string().nullable(),
    branchId: objectId,
    locationId: objectId,
    direction: z.enum(TIMECLOCK_DIRECTIONS),
    ts: z.coerce.date(),
    method: z.enum(TIMECLOCK_METHODS),
    correctionOfEntryId: objectId.nullable(),
    correctionReason: z.string().nullable(),
    /** Entrada sin salida cerrada al fin del turno, marcada (RN-PRO-08). */
    orphanClosed: z.boolean(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Contingencia (RN-ACC-24/25)
// ─────────────────────────────────────────────────────────────────────────────
/** Activar/desactivar el modo contingencia de un LOCAL entero (RN-ACC-24). */
export const contingencyBody = z.object({
    locationId: objectId,
    active: z.boolean(),
    reason: reasonRequired,
});
export const accessSnapshotQuery = z.object({ locationId: objectId });
/** Snapshot de habilitados: verde/amarillo/rojo por persona + huellas del local. */
export const accessSnapshotFullResponse = z.object({
    generatedAt: z.coerce.date(),
    locationId: objectId,
    /** Estado del punto de acceso al generar el snapshot (online/offline/contingency). */
    state: z.string(),
    entries: z.array(z.object({
        personId: objectId,
        kind: z.enum(["member", "visitor"]),
        name: z.string(),
        memberNumber: z.number().int().nullable(),
        docNumber: z.string().nullable(),
        light: z.enum(ACCESS_LIGHTS),
        reason: z.string().nullable(),
        badges: z.array(z.string()),
        /** Handles de las huellas enroladas: es lo que el lector devuelve. */
        fingerprints: z.array(z.string()),
    })),
});
/** Cola offline del agente y de la SPA (RN-ACC-25). */
export const contingencySyncFullBody = z.object({
    entries: z
        .array(z.object({
        accessPointId: objectId,
        personId: objectId.optional(),
        personKind: z.enum(["member", "visitor", "trial", "staff", "unknown"]).optional(),
        method: z.enum(ACCESS_METHODS),
        identifier: z.string().max(512).optional(),
        ts: dateISO,
        /** Cargado a mano por recepción: quién lo cargó. */
        manualByUserId: objectId.optional(),
    }))
        .min(1)
        .max(1000),
});
export const contingencySyncResponse = z.object({
    synced: z.number().int().min(0),
    /** Ingresos que HOY habrían sido rechazados: se avisan, no se bloquean (RN-ACC-25). */
    wouldHaveBeenRejected: z.array(z.object({ accessLogId: objectId, personId: objectId.nullable(), reason: z.string() })),
});
/** Lote de presentes que el portal profesores registra en un local sin recepción caído. */
export const teacherPresenceBatchBody = z.object({
    locationId: objectId,
    entries: z
        .array(z.object({ personId: objectId, kind: z.enum(["member", "visitor"]), ts: dateISO }))
        .min(1)
        .max(200),
    reason: reasonRequired,
});
// ─────────────────────────────────────────────────────────────────────────────
// App de socios (RN-ACC-03, carnet)
// ─────────────────────────────────────────────────────────────────────────────
/** `POST /api/app/access/location-qr` — la app manda el QR crudo o sólo el secreto. */
export const locationQrScanBody = z
    .object({
    venueQr: z.string().min(6).max(256).optional(),
    secret: z.string().min(8).max(256).optional(),
    accessPointId: objectId.optional(),
})
    .refine((v) => Boolean(v.venueQr ?? v.secret), {
    message: "Falta el contenido del QR del local",
});
export const locationQrScanResponse = z.object({
    result: z.enum(ACCESS_RESULTS),
    light: z.enum(ACCESS_LIGHTS),
    reason: z.string().nullable(),
    reasonCode: z.enum(ACCESS_REASONS).nullable(),
    message: z.string(),
    action: z.object({ kind: z.enum(["pay", "upload_certificate", "reception", "none"]) }),
    accessLogId: objectId.nullable(),
    /** RN-ACC-16: la clase a la que el ingreso le marcó asistencia, o `null`. */
    markedAttendance: z
        .object({
        bookingId: objectId,
        sessionId: objectId.nullable(),
        /** Nombre de la disciplina de la clase marcada («Pilates Reformer»). */
        className: z.string().nullable(),
        startsAt: z.coerce.date().nullable(),
    })
        .nullable(),
});
/** `GET /api/app/access/status` — semáforo personal del carnet. */
export const accessStatusResponse = z.object({
    overall: z.enum(ACCESS_LIGHTS),
    items: z.array(z.object({
        key: z.enum(["dues", "medical", "plan", "frequency", "admission", "freeze"]),
        level: z.enum(ACCESS_LIGHTS),
        title: z.string(),
        detail: z.string(),
        action: z.object({ kind: z.enum(["pay", "upload_certificate", "reception", "none"]) }),
    })),
    /** Frecuencia semanal (RN-ACC-11). `limit: null` = sin tope. */
    weekly: z.object({
        limit: z.number().int().nullable(),
        used: z.number().int(),
        weekFrom: z.coerce.date(),
        weekTo: z.coerce.date(),
    }),
    badges: z.array(z.string()),
});
//# sourceMappingURL=acceso.js.map